/**
 * DecisionFlow — PodManager
 * ui/src/components/tenant-admin/PodManager.jsx
 *
 * Domain: §P4-07 Pod / Team
 * Nav route: /admin/pods
 * API:
 *   GET  /api/v1/comp-pods
 *   POST /api/v1/comp-pods
 *   GET  /api/v1/comp-pod-members?pod_id={id}
 *   POST /api/v1/comp-pod-members
 *   PATCH /api/v1/comp-pod-members/{id}
 *   GET  /api/v1/comp-pod-quotas?pod_id={id}
 *   POST /api/v1/comp-pod-quotas
 *   POST /api/v1/comp-pod-quotas/{id}/approve
 * Feature gate: Standard — no gate.
 * Tabs: Pods | Members | Quotas
 * Quota approval is the key gate. Pod and member create/edit have no approval workflow.
 * State derived from approved_by / submitted_at — no status column (quota only).
 * Path: ui/src/components/tenant-admin/PodManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'pods',    label: 'Pods'    },
  { key: 'members', label: 'Members' },
  { key: 'quotas',  label: 'Quotas'  },
];

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_POD = {
  pod_name:    '',
  pod_type:    '',
  description: '',
};

const EMPTY_MEMBER = {
  position_id:  '',
  member_role:  '',
  split_pct:    '',
  start_date:   '',
  end_date:     '',
};

const EMPTY_QUOTA = {
  quota_amount:      '',
  currency:          'USD',
  effective_date:    '',
  expiration_date:   '',
};

// ---------------------------------------------------------------------------
// PodManager
// ---------------------------------------------------------------------------

export default function PodManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.pod')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }
  const fpId = period?.fiscal_period_id ?? fiscalPeriodId ?? null;

  const [activeTab, setActiveTab] = useState('pods');

  // ── Pods ──
  const [pods, setPods]                     = useState([]);
  const [podsLoading, setPodsLoading]       = useState(false);
  const [podsError, setPodsError]           = useState('');
  const [selectedPod, setSelectedPod]       = useState(null);
  const [showPodForm, setShowPodForm]       = useState(false);
  const [podForm, setPodForm]               = useState(EMPTY_POD);
  const [podSaving, setPodSaving]           = useState(false);
  const [podFormError, setPodFormError]     = useState('');

  // ── Members ──
  const [members, setMembers]               = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError]     = useState('');
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [memberForm, setMemberForm]         = useState(EMPTY_MEMBER);
  const [memberSaving, setMemberSaving]     = useState(false);
  const [memberFormError, setMemberFormError] = useState('');
  const [editingMember, setEditingMember]   = useState(null);

  // ── Quotas ──
  const [quotas, setQuotas]                 = useState([]);
  const [quotasLoading, setQuotasLoading]   = useState(false);
  const [quotasError, setQuotasError]       = useState('');
  const [selectedQuota, setSelectedQuota]   = useState(null);
  const [showQuotaForm, setShowQuotaForm]   = useState(false);
  const [quotaForm, setQuotaForm]           = useState(EMPTY_QUOTA);
  const [quotaSaving, setQuotaSaving]       = useState(false);
  const [quotaFormError, setQuotaFormError] = useState('');
  const [quotaActionLoading, setQuotaActionLoading] = useState(false);
  const [quotaActionError, setQuotaActionError]     = useState('');

  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Load pods
  // ---------------------------------------------------------------------------

  const loadPods = useCallback(async () => {
    if (!user) return;
    setPodsLoading(true);
    setPodsError('');
    try {
      const data = await apiGet('/api/v1/comp-pods');
      setPods(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      setPodsError(getErrorMessage(error, 'Unable to load pods.'));
    } finally {
      setPodsLoading(false);
    }
  }, [user]);

  useEffect(() => { loadPods(); }, [loadPods]);

  // ---------------------------------------------------------------------------
  // Load members for selected pod
  // ---------------------------------------------------------------------------

  const loadMembers = useCallback(async (podId) => {
    if (!podId) return;
    setMembersLoading(true);
    setMembersError('');
    try {
      const data = await apiGet(`/api/v1/comp-pod-members?pod_id=${podId}`);
      setMembers(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      setMembersError(getErrorMessage(error, 'Unable to load pod members.'));
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'members' && selectedPod) {
      loadMembers(selectedPod.pod_id);
    }
  }, [activeTab, selectedPod, loadMembers]);

  // ---------------------------------------------------------------------------
  // Load quotas for selected pod
  // ---------------------------------------------------------------------------

  const loadQuotas = useCallback(async (podId) => {
    if (!podId) return;
    setQuotasLoading(true);
    setQuotasError('');
    try {
      const data = await apiGet(`/api/v1/comp-pod-quotas?pod_id=${podId}`);
      setQuotas(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      setQuotasError(getErrorMessage(error, 'Unable to load pod quotas.'));
    } finally {
      setQuotasLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'quotas' && selectedPod) {
      loadQuotas(selectedPod.pod_id);
    }
  }, [activeTab, selectedPod, loadQuotas]);

  // ---------------------------------------------------------------------------
  // Pod actions
  // ---------------------------------------------------------------------------

  async function handleCreatePod(e) {
    e.preventDefault();
    if (!podForm.pod_name.trim()) { setPodFormError('Pod name is required.'); return; }
    setPodSaving(true); setPodFormError('');
    try {
      await apiPost('/api/v1/comp-pods', {
        pod_name:    podForm.pod_name.trim(),
        pod_type:    podForm.pod_type.trim() || null,
        description: podForm.description.trim() || null,
      });
      setShowPodForm(false); setPodForm(EMPTY_POD);
      showToast('Pod created.');
      await loadPods();
    } catch (error) {
      setPodFormError(getErrorMessage(error, 'Failed to create pod.'));
    } finally {
      setPodSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Member actions
  // ---------------------------------------------------------------------------

  async function handleCreateMember(e) {
    e.preventDefault();
    if (!selectedPod) { setMemberFormError('Select a pod first.'); return; }
    if (!memberForm.position_id.trim()) { setMemberFormError('Position ID is required.'); return; }
    setMemberSaving(true); setMemberFormError('');
    try {
      await apiPost('/api/v1/comp-pod-members', {
        pod_id:       selectedPod.pod_id,
        position_id:  memberForm.position_id.trim(),
        member_role:  memberForm.member_role.trim() || null,
        split_pct:    memberForm.split_pct ? parseFloat(memberForm.split_pct) : null,
        start_date:   memberForm.start_date || null,
        end_date:     memberForm.end_date || null,
      });
      setShowMemberForm(false); setMemberForm(EMPTY_MEMBER);
      showToast('Member added.');
      await loadMembers(selectedPod.pod_id);
    } catch (error) {
      setMemberFormError(getErrorMessage(error, 'Failed to add member.'));
    } finally {
      setMemberSaving(false);
    }
  }

  async function handleUpdateMember(memberId, patch) {
    try {
      await apiPost(`/api/v1/comp-pod-members/${memberId}`, { _method: 'PATCH', ...patch });
      showToast('Member updated.');
      if (selectedPod) await loadMembers(selectedPod.pod_id);
    } catch (error) {
      showToast('Failed to update member.');
    }
  }

  // ---------------------------------------------------------------------------
  // Quota actions
  // ---------------------------------------------------------------------------

  async function handleCreateQuota(e) {
    e.preventDefault();
    if (!selectedPod) { setQuotaFormError('Select a pod first.'); return; }
    if (!quotaForm.quota_amount) { setQuotaFormError('Quota amount is required.'); return; }
    setQuotaSaving(true); setQuotaFormError('');
    try {
      await apiPost('/api/v1/comp-pod-quotas', {
        pod_id:           selectedPod.pod_id,
        fiscal_period_id: fpId,
        quota_amount:     parseFloat(quotaForm.quota_amount),
        currency:         quotaForm.currency || 'USD',
        effective_date:   quotaForm.effective_date || null,
        expiration_date:  quotaForm.expiration_date || null,
      });
      setShowQuotaForm(false); setQuotaForm(EMPTY_QUOTA);
      showToast('Pod quota created.');
      await loadQuotas(selectedPod.pod_id);
    } catch (error) {
      setQuotaFormError(getErrorMessage(error, 'Failed to create quota.'));
    } finally {
      setQuotaSaving(false);
    }
  }

  async function handleQuotaSubmit() {
    if (!selectedQuota) return;
    setQuotaActionLoading(true); setQuotaActionError('');
    try {
      await apiPost(`/api/v1/comp-pod-quotas/${selectedQuota.pod_quota_id}/submit`);
      showToast('Submitted for approval.');
      await loadQuotas(selectedPod.pod_id);
      setSelectedQuota((prev) => ({ ...prev, submitted_at: new Date().toISOString() }));
    } catch (error) {
      setQuotaActionError(getErrorMessage(error, 'Failed to submit.'));
    } finally {
      setQuotaActionLoading(false);
    }
  }

  async function handleQuotaApprove() {
    if (!selectedQuota) return;
    setQuotaActionLoading(true); setQuotaActionError('');
    try {
      await apiPost(`/api/v1/comp-pod-quotas/${selectedQuota.pod_quota_id}/approve`);
      showToast('Pod quota approved.');
      await loadQuotas(selectedPod.pod_id);
      setSelectedQuota((prev) => ({ ...prev, approved_by: user?.user_id || user?.id }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_NOT_ALLOWED') {
        setQuotaActionError('Self-approval is not permitted (NN-10).');
      } else {
        setQuotaActionError(getErrorMessage(error, 'Failed to approve.'));
      }
    } finally {
      setQuotaActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) {
    return (
      <section className="flex flex-col gap-4">
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // No-pod-selected guard for Members / Quotas tabs
  // ---------------------------------------------------------------------------

  function NoPodSelected() {
    return (
      <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
        Select a pod in the Pods tab first.
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">Pod / Team</h1>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-tok-sm font-medium border-b-2 transition ${
              activeTab === t.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
            }`}
          >
            {t.label}
            {t.key !== 'pods' && selectedPod && (
              <span className="ml-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                ({selectedPod.pod_name})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Pods tab ── */}
      {activeTab === 'pods' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Compensation Pods</h2>
            {!readOnly && (
              <button
                type="button"
                onClick={() => { setShowPodForm((v) => !v); setPodFormError(''); setPodForm(EMPTY_POD); }}
                className={btnPrimary}
              >
                {showPodForm ? 'Cancel' : '+ New Pod'}
              </button>
            )}
          </div>

          {showPodForm && (
            <form onSubmit={handleCreatePod} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Pod</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className={labelClass}>
                  Pod Name <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" value={podForm.pod_name} onChange={(e) => setPodForm((f) => ({ ...f, pod_name: e.target.value }))} placeholder="e.g. East Region Pod" className={inputClass} required />
                </label>
                <label className={labelClass}>
                  Pod Type
                  <input type="text" value={podForm.pod_type} onChange={(e) => setPodForm((f) => ({ ...f, pod_type: e.target.value }))} placeholder="e.g. overlay, primary" className={inputClass} />
                </label>
                <label className={labelClass + ' sm:col-span-2'}>
                  Description
                  <textarea value={podForm.description} onChange={(e) => setPodForm((f) => ({ ...f, description: e.target.value }))} rows={2} className={inputClass} />
                </label>
              </div>
              {podFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{podFormError}</div>
              )}
              <div className="flex gap-2">
                <button type="submit" disabled={podSaving} className={btnPrimary}>{podSaving ? 'Saving…' : 'Create Pod'}</button>
                <button type="button" onClick={() => { setShowPodForm(false); setPodFormError(''); }} className={btnSecondary}>Cancel</button>
              </div>
            </form>
          )}

          {podsLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
          {podsError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{podsError}</div>}
          {!podsLoading && !podsError && pods.length === 0 && (
            <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No pods configured</p>
              <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Create a pod to group positions for team-based compensation.</p>
            </div>
          )}
          {pods.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Pod Name</th>
                    <th className={thClass}>Type</th>
                    <th className={thClass}>Description</th>
                    <th className={thClass}>Selected</th>
                  </tr>
                </thead>
                <tbody>
                  {pods.map((p) => {
                    const isSelected = selectedPod?.pod_id === p.pod_id;
                    return (
                      <tr
                        key={p.pod_id}
                        className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${isSelected ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                        onClick={() => setSelectedPod(isSelected ? null : p)}
                      >
                        <td className={tdClass + ' font-medium'}>{p.pod_name}</td>
                        <td className={tdClass}>{p.pod_type ?? '—'}</td>
                        <td className={tdClass + ' max-w-xs truncate'}>{p.description ?? '—'}</td>
                        <td className={tdClass}>
                          {isSelected && (
                            <span className="inline-flex items-center rounded-full bg-[rgb(var(--color-primary))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-primary))]">
                              Active
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {selectedPod && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Pod <strong className="text-[rgb(var(--color-text))]">{selectedPod.pod_name}</strong> selected — switch to Members or Quotas tabs to configure.
            </p>
          )}
        </div>
      )}

      {/* ── Members tab ── */}
      {activeTab === 'members' && (
        <div className="flex flex-col gap-4">
          {!selectedPod ? <NoPodSelected /> : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Members — {selectedPod.pod_name}</h2>
                {!readOnly && (
                  <button type="button" onClick={() => { setShowMemberForm((v) => !v); setMemberFormError(''); setMemberForm(EMPTY_MEMBER); setEditingMember(null); }} className={btnPrimary}>
                    {showMemberForm ? 'Cancel' : '+ Add Member'}
                  </button>
                )}
              </div>

              {showMemberForm && (
                <form onSubmit={handleCreateMember} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">Add Pod Member</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className={labelClass}>
                      Position ID <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input type="text" value={memberForm.position_id} onChange={(e) => setMemberForm((f) => ({ ...f, position_id: e.target.value }))} placeholder="e.g. POS-0042" className={inputClass} required />
                    </label>
                    <label className={labelClass}>
                      Member Role
                      <input type="text" value={memberForm.member_role} onChange={(e) => setMemberForm((f) => ({ ...f, member_role: e.target.value }))} placeholder="e.g. lead, contributor" className={inputClass} />
                    </label>
                    <label className={labelClass}>
                      Split %
                      <input type="number" step="0.01" min="0" max="100" value={memberForm.split_pct} onChange={(e) => setMemberForm((f) => ({ ...f, split_pct: e.target.value }))} placeholder="0.00" className={inputClass} />
                    </label>
                    <div />
                    <label className={labelClass}>
                      Start Date
                      <input type="date" value={memberForm.start_date} onChange={(e) => setMemberForm((f) => ({ ...f, start_date: e.target.value }))} className={inputClass} />
                    </label>
                    <label className={labelClass}>
                      End Date
                      <input type="date" value={memberForm.end_date} onChange={(e) => setMemberForm((f) => ({ ...f, end_date: e.target.value }))} className={inputClass} />
                    </label>
                  </div>
                  {memberFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{memberFormError}</div>}
                  <div className="flex gap-2">
                    <button type="submit" disabled={memberSaving} className={btnPrimary}>{memberSaving ? 'Saving…' : 'Add Member'}</button>
                    <button type="button" onClick={() => { setShowMemberForm(false); setMemberFormError(''); }} className={btnSecondary}>Cancel</button>
                  </div>
                </form>
              )}

              {membersLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
              {membersError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{membersError}</div>}
              {!membersLoading && !membersError && members.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No members. Add positions to this pod.</p>
                </div>
              )}
              {members.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Position ID</th>
                        <th className={thClass}>Role</th>
                        <th className={thClass}>Split %</th>
                        <th className={thClass}>Start</th>
                        <th className={thClass}>End</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <tr key={m.pod_member_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass + ' font-medium'}>{m.position_id}</td>
                          <td className={tdClass}>{m.member_role ?? '—'}</td>
                          <td className={tdClass}>{m.split_pct != null ? `${m.split_pct}%` : '—'}</td>
                          <td className={tdClass}>{m.start_date ?? '—'}</td>
                          <td className={tdClass}>{m.end_date ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Quotas tab ── */}
      {activeTab === 'quotas' && (
        <div className="flex flex-col gap-4">
          {!selectedPod ? <NoPodSelected /> : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Quotas — {selectedPod.pod_name}</h2>
                {!readOnly && (
                  <button type="button" onClick={() => { setShowQuotaForm((v) => !v); setQuotaFormError(''); setQuotaForm(EMPTY_QUOTA); }} className={btnPrimary}>
                    {showQuotaForm ? 'Cancel' : '+ Add Quota'}
                  </button>
                )}
              </div>

              {showQuotaForm && (
                <form onSubmit={handleCreateQuota} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Pod Quota</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className={labelClass}>
                      Quota Amount <span className="text-[rgb(var(--color-danger))]">*</span>
                      <input type="number" step="0.01" min="0" value={quotaForm.quota_amount} onChange={(e) => setQuotaForm((f) => ({ ...f, quota_amount: e.target.value }))} placeholder="0.00" className={inputClass} required />
                    </label>
                    <label className={labelClass}>
                      Currency
                      <input type="text" maxLength={3} value={quotaForm.currency} onChange={(e) => setQuotaForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} placeholder="USD" className={inputClass} />
                    </label>
                    <label className={labelClass}>
                      Effective Date
                      <input type="date" value={quotaForm.effective_date} onChange={(e) => setQuotaForm((f) => ({ ...f, effective_date: e.target.value }))} className={inputClass} />
                    </label>
                    <label className={labelClass}>
                      Expiration Date
                      <input type="date" value={quotaForm.expiration_date} onChange={(e) => setQuotaForm((f) => ({ ...f, expiration_date: e.target.value }))} className={inputClass} />
                    </label>
                  </div>
                  {quotaFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{quotaFormError}</div>}
                  <div className="flex gap-2">
                    <button type="submit" disabled={quotaSaving} className={btnPrimary}>{quotaSaving ? 'Saving…' : 'Create Quota'}</button>
                    <button type="button" onClick={() => { setShowQuotaForm(false); setQuotaFormError(''); }} className={btnSecondary}>Cancel</button>
                  </div>
                </form>
              )}

              {quotasLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
              {quotasError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{quotasError}</div>}
              {!quotasLoading && !quotasError && quotas.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No pod quotas. Add a quota to define the team target.</p>
                </div>
              )}
              {quotas.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Amount</th>
                        <th className={thClass}>Currency</th>
                        <th className={thClass}>Effective</th>
                        <th className={thClass}>Expires</th>
                        <th className={thClass}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotas.map((q) => (
                        <tr
                          key={q.pod_quota_id}
                          className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedQuota?.pod_quota_id === q.pod_quota_id ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                          onClick={() => { setSelectedQuota(q); setQuotaActionError(''); }}
                        >
                          <td className={tdClass + ' font-medium'}>{q.quota_amount != null ? Number(q.quota_amount).toLocaleString() : '—'}</td>
                          <td className={tdClass}>{q.currency ?? '—'}</td>
                          <td className={tdClass}>{q.effective_date ?? '—'}</td>
                          <td className={tdClass}>{q.expiration_date ?? '—'}</td>
                          <td className={tdClass}><StatusPill status={deriveState(q)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedQuota && !readOnly && (() => {
                const state = deriveState(selectedQuota);
                return (
                  <div className="flex flex-col gap-3">
                    <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
                      Selected quota: <strong className="text-[rgb(var(--color-text))]">{selectedQuota.quota_amount != null ? Number(selectedQuota.quota_amount).toLocaleString() : '—'} {selectedQuota.currency}</strong>
                    </div>
                    {quotaActionError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{quotaActionError}</div>}
                    <ApprovalPanel
                      variant="pod_quota"
                      currentState={state}
                      submittedBy={selectedQuota.submitted_by}
                      currentUserId={currentUserId}
                      onSubmit={state === 'draft' ? handleQuotaSubmit : null}
                      onApprove={state === 'pending_approval' ? handleQuotaApprove : null}
                      onReject={null}
                      approvedBy={selectedQuota.approved_by}
                      approvedAt={selectedQuota.approved_at}
                      loading={quotaActionLoading}
                    />
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg">
          {toast}
        </div>
      )}
    </section>
  );
}
